import { Component, OnInit, OnDestroy, Input, Output, ViewChild, EventEmitter, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { Subscription, pipe, finalize, concatMap, Subject, of } from 'rxjs';
import { map, filter, switchMap, debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { DropdownModule } from 'primeng/dropdown';
import { ClientApiService } from 'src/app/services/client-api.service';

/**
 * Reusable customer vertical-market (business area) dropdown selector backed by `ClientApiService`.
 *
 * Two pipelines run in parallel after init:
 * - `load$` — debounced `switchMap(getCustomerBusinessAreaByID)` — resolves the currently selected
 *   business area for the given `customerId`.
 * - `area$` — debounced `switchMap(emit)` — emits `selectedBusinessArea` after click/change/clear.
 *
 * On init `_reloadComponentData()` fetches the full list of known business areas for the dropdown,
 * and `load$.next(customerId)` triggers an initial customer-specific lookup.
 *
 * On `customerId` input change the component pushes a new ID onto `load$` without reloading the
 * static area list.
 *
 * **Output:** `selectedBusinessArea` emits the selected vertical-market string on user interaction.
 */
@Component({
  selector: 'app-customer-business-area-selector',
  templateUrl: './customer-business-area-selector.component.html',
  styleUrls: ['./customer-business-area-selector.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class CustomerBusinessAreaSelectorComponent implements OnInit, OnChanges, OnDestroy {

  _loading: boolean;

  customerBusinessAreas: string[] = [];
  customerBusinessArea: string = '';

  /** The client ID used to look up the customer's currently assigned business area. */
  @Input() customerId: number;

  @ViewChild('customerBaSelector') CurrencySelector: DropdownModule;

  /** Label text displayed above the dropdown. */
  @Input() labelKey = 'Vertical market';
  /** Placeholder text shown inside the dropdown when nothing is selected. */
  @Input() placeholder = 'Select market...';

  _disabled: boolean = false;
  /** When `true`, the dropdown is rendered as read-only. */
  @Input() get disabled(): boolean { return this._disabled; }
           set disabled(b: boolean) { this._disabled = b; }

  _showLabel: boolean = true;
  /** When `false`, the label above the dropdown is hidden. */
  @Input() get showLabel(): boolean { return this._showLabel; }
           set showLabel(b: boolean) { this._showLabel = b; }

  /** Emits the selected vertical-market string on user interaction. */
  @Output() public selectedBusinessArea: EventEmitter<string> = new EventEmitter();

  /** When `true`, the Edit button is rendered (currently unused in template). */
  @Input() showEditButton: boolean = true;
  /** When `true`, the Add Rate button is rendered (currently unused in template). */
  @Input() showAddRateButton: boolean = true;
  /** When `true`, the Add Area button is rendered. */
  @Input() showAddCurrencyButton: boolean = true;

  public get doShowEditButton(): boolean { return this.showEditButton; };
  public get doShowAddRateButton(): boolean { return this.showAddRateButton; };
  public get doShowAddCurrencyButton(): boolean { return this.showAddCurrencyButton; };

  /** `true` when `customerBusinessArea` matches an entry in the loaded `customerBusinessAreas` list. */
  public get areaExists(): boolean { return this.customerBusinessAreas.find(e => e == this.customerBusinessArea) != null; };

  private load$ = new Subject <number>();
  private area$ = new Subject <string>();

  private _getCurrencyQuery: Subscription;
  private _selCurrencyQuery: Subscription;
  private _gelCurrencyQuery: Subscription;

  constructor(private cliApi: ClientApiService) { }

  /** Reserved for post-load row resolution — currently a no-op (body is commented out). */
  private selectRow(): void {

//    this.customerBusinessArea = (this._selectedID && this._currList) ? this._currList.find(x => x.CURID == this._selectedID) : null;

//    console.log('*** CustomerBusinessAreaSelectorComponent _selectCurrency: ' + JSON.stringify(this._selectedCurrency));
//    this.emitCurrency();
  }

  /** Loads the full list of known business areas (for the dropdown options) from the API. */
  private _reloadComponentData(): void {
    this._loading = true;
    this._getCurrencyQuery =
      this.cliApi.getCustomerBusinessAreaList()
        .pipe(
          //tap(a => console.log('*** CustomerBusinessAreaSelectorComponent customerBusinessAreas: ' + JSON.stringify(a))),
          //tap(a => console.table(a)),
          concatMap(data => this.customerBusinessAreas = data.map(a => a['CUSTOMERTYPE'])),

          finalize(() => {
            this._loading = false;
            this.selectRow();
          })
        )
        .subscribe();
  }

  /** Reacts to `customerId` changes by pushing the new ID onto `load$` for customer-specific lookup. */
  ngOnChanges(changes: SimpleChanges) {
    //console.log('*** CustomerBusinessAreaSelectorComponent ngOnChanges');
    for (let propName in changes) {
      let change = changes[propName];

      console.log('*** CustomerBusinessAreaSelectorComponent ngOnChanges (actual): ' + propName + ' ' + JSON.stringify(change.currentValue));
      //console.log('*** CustomerBusinessAreaSelectorComponent ngOnChanges (was):    ' + propName + ' ' + JSON.stringify(change.previousValue));

      switch (propName) {
        case 'customerId':
          this.load$.next(change.currentValue);
          break;
      }
    }
  }

  /** Unsubscribes all active pipeline subscriptions to prevent memory leaks. */
  ngOnDestroy(): void {
    if (this._getCurrencyQuery) { this._getCurrencyQuery.unsubscribe(); }
    if (this._selCurrencyQuery) { this._selCurrencyQuery.unsubscribe(); }
    if (this._gelCurrencyQuery) { this._gelCurrencyQuery.unsubscribe(); }
  }

  /**
   * Sets up the `load$` (customer lookup) and `area$` (emit) pipelines, loads the area list,
   * and triggers the initial customer-specific lookup.
   */
  ngOnInit(): void {
    console.log('*** CustomerBusinessAreaSelectorComponent ngOnInit: ');

    this._selCurrencyQuery = this.load$
      .pipe(

        debounceTime(50),
        distinctUntilChanged(),

        tap(() => this._loading = true),

        switchMap(custid => this.cliApi.getCustomerBusinessAreaByID(custid).pipe(
          tap(a => console.log('*** CustomerBusinessAreaSelectorComponent _reloadComponentData: ' + JSON.stringify(a))),

          map(data => this.customerBusinessArea = data.map(a => a['CUSTOMERTYPE'])[0]),

          finalize(() => {
            this._loading = false;
            this.selectRow();
          })
        )),
      )
      .subscribe();

    this._gelCurrencyQuery = this.area$
      .pipe(

        debounceTime(150),
        distinctUntilChanged(),

        switchMap(text => of(text).pipe(
          tap(text => this.selectedBusinessArea.emit(text))
        )),
      )
      .subscribe();

    this._reloadComponentData();

    this.load$.next(this.customerId);
  }

  /** Handles dropdown `(onClick)` — pushes current area onto `area$` to trigger emit. */
  onClickCS(e) {
    console.log('*** CustomerBusinessAreaSelectorComponent onClickCS: ' + JSON.stringify(e))

    this.area$.next(this.customerBusinessArea);
  }

  /** Handles dropdown `(onChange)` — pushes current area onto `area$` to trigger emit. */
  onChangeCS(e) {
    console.log('*** CustomerBusinessAreaSelectorComponent onChangeCS: ' + JSON.stringify(e));

    this.area$.next(this.customerBusinessArea);
  }

  /** Handles dropdown `(onClear)` — pushes current area onto `area$` to trigger emit. */
  onClearCS(e) {
    console.log('*** CustomerBusinessAreaSelectorComponent onClearCS: ' + JSON.stringify(e));

    this.area$.next(this.customerBusinessArea);
  }

  /** Add-area button handler — currently a no-op (body is empty). */
  onCurrencyAddButton(e) {
    console.log('*** CustomerBusinessAreaSelectorComponent onCurrencyAddButton: ' + JSON.stringify(e));
    //console.log('*** CustomerBusinessAreaSelectorComponent onCurrencyAddButton: about to add currency ');
  }
}
